import { classifyTimingShape, MAX_DAYS_PER_WEEK } from '@liza/fhir-utils';
import type { FhirTimingRepeat } from '@liza/fhir-utils';
import { format, parseISO } from 'date-fns';
import { z } from 'zod';
import { DATE_FORMATS } from './date';

/** Validates an optional YYYY-MM-DD date string (or empty string). Rejects invalid calendar dates like 2026-02-30. */
export const DueDateFieldSchema = z
  .string()
  .refine((s) => s === '' || (/^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(parseISO(s).getTime())), {
    message: 'Invalid date',
  });

// ============================================================================
// Types
// ============================================================================

export type ScheduleType = 'once' | 'asNeeded' | 'simple' | 'daysPerWeek' | 'custom';

/**
 * The frequency options every care plan item type offers, in the order they are presented: Days of
 * week, Days per week, Every…, As needed, Once (#8100).
 *
 * One list rather than a literal per form — the four modals had drifted apart (the admin prescribe
 * path was missing two options that the records dialog offered for the same drug), which is the bug
 * #8100 exists to fix. A form that genuinely offers a narrower set passes its own array and says why;
 * `ScheduleTypeTabs` filters this order regardless, so no caller can reorder the tabs.
 */
export const SHARED_SCHEDULE_TYPES: readonly ScheduleType[] = ['simple', 'daysPerWeek', 'custom', 'asNeeded', 'once'];
export type CustomUnit = 'hours' | 'days' | 'weeks' | 'months' | 'years';
/** Single time-of-day annotation for the days-per-week and Every… modes. */
export type TimeOfDayCode = 'MORN' | 'AFT' | 'EVE' | 'NIGHT';
/** How a recurring schedule ends: never, on a calendar date, or after N occurrences. */
export type ScheduleEndMode = 'never' | 'onDate' | 'afterCount';

export type TimingRepeatValue = FhirTimingRepeat;

/** Wider timing shape for initialTiming — output types have optional frequency/period/periodUnit. */
export type InitialTimingValue = Partial<Pick<TimingRepeatValue, 'frequency' | 'period' | 'periodUnit'>> &
  Omit<TimingRepeatValue, 'frequency' | 'period' | 'periodUnit'>;

export interface SchedulePickerState {
  scheduleType: ScheduleType;
  selectedDays: boolean[];
  /** Target for the days-per-week quota mode (1–7; 7 normalizes to daily). */
  daysPerWeekCount: number;
  customInterval: number;
  customUnit: CustomUnit;
  /** Specific clock time (HH:MM) for the weekday shape; retained from stored
   *  `timeOfDay` data — chips replace it the moment one is selected. */
  specificTime: string;
  /** Selected time-of-day chips. On Days of week, N codes = N times per day
   *  (the only way to express per-day multiplicity); on the days-per-week and
   *  Every… modes at most one code applies as the canonical annotation. */
  whenCodes: TimeOfDayCode[];
  startDate: string;
  endMode: ScheduleEndMode;
  /** YYYY-MM-DD; used when `endMode` is 'onDate'. */
  endDate: string;
  /** Occurrence count; used when `endMode` is 'afterCount'. */
  endCount: number;
}

// ============================================================================
// Constants
// ============================================================================

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const FULL_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** Canonical order for `when[]` codes, morning-first. */
export const WHEN_CODE_ORDER: TimeOfDayCode[] = ['MORN', 'AFT', 'EVE', 'NIGHT'];

/** Sort selected chips into the canonical morning-first order. */
export function sortWhenCodes(codes: readonly TimeOfDayCode[]): TimeOfDayCode[] {
  return WHEN_CODE_ORDER.filter((code) => codes.includes(code));
}

/** Default `when[]` codes when normalizing a bare "N times per day" (no codes stored). */
const DEFAULT_WHEN_BY_COUNT: Partial<Record<number, TimeOfDayCode[]>> = {
  2: ['MORN', 'EVE'],
  3: ['MORN', 'AFT', 'EVE'],
  4: ['MORN', 'AFT', 'EVE', 'NIGHT'],
};

export const FHIR_WHEN_CODES = new Set(['MORN', 'AFT', 'EVE', 'NIGHT']);

/** The "every N hours" preset intervals the Every… mode offers (design doc: hourly stays preset-only). */
export const HOURLY_PRESET_OPTIONS = [1, 2, 4, 6, 8, 12] as const;

/** Snap an arbitrary interval to the nearest hourly preset. */
export function nearestHourlyPreset(interval: number): number {
  return [...HOURLY_PRESET_OPTIONS].sort((a, b) => Math.abs(a - interval) - Math.abs(b - interval))[0]!;
}

/** FHIR period unit for each Custom ("Every…") interval unit. */
const CUSTOM_UNIT_TO_PERIOD_UNIT: Record<CustomUnit, NonNullable<TimingRepeatValue['periodUnit']>> = {
  hours: 'h',
  days: 'd',
  weeks: 'wk',
  months: 'mo',
  years: 'a',
};

/** Inverse of {@link CUSTOM_UNIT_TO_PERIOD_UNIT}, for deriving edit-mode state. */
const PERIOD_UNIT_TO_CUSTOM_UNIT: Partial<Record<string, CustomUnit>> = {
  h: 'hours',
  d: 'days',
  wk: 'weeks',
  mo: 'months',
  a: 'years',
};

/** Default quota when switching into days-per-week mode (matches the #4921 mock example). */
const DEFAULT_DAYS_PER_WEEK = 3;

/** Default occurrence count when switching the end mode to "after N times". */
const DEFAULT_END_COUNT = 10;

/** The modes that repeat (and therefore offer time-of-day / starts / ends). */
export function isRecurringScheduleType(scheduleType: ScheduleType): boolean {
  return scheduleType === 'simple' || scheduleType === 'daysPerWeek' || scheduleType === 'custom';
}

// ============================================================================
// Helpers
// ============================================================================

export function makeDaySelection(dayOfWeek?: string[]): boolean[] {
  if (!dayOfWeek || dayOfWeek.length === 0) {
    return Array(7).fill(true) as boolean[];
  }
  const daySet = new Set(dayOfWeek);
  return DAY_KEYS.map((key) => daySet.has(key));
}

/** The canonical daily shape: a period-1 day cadence with all seven days listed. */
function dailyAllDays(): TimingRepeatValue {
  return { frequency: 1, period: 1, periodUnit: 'd', dayOfWeek: [...DAY_KEYS] };
}

function withStartDate(timing: TimingRepeatValue, startDate: string): TimingRepeatValue {
  if (startDate) timing.boundsPeriod = { start: parseISO(startDate) };
  return timing;
}

/** Attach the single time-of-day annotation the quota/interval shapes support. */
function withTimeOfDay(timing: TimingRepeatValue, code: TimeOfDayCode | ''): TimingRepeatValue {
  if (code) timing.when = [code];
  return timing;
}

/**
 * Attach the schedule's end: a calendar end date, or an occurrence count
 * ("ends after N times"). The days-per-week quota never writes a count — its
 * "times" are completions, which structural expansion cannot count.
 */
function withScheduleEnd(timing: TimingRepeatValue, state: SchedulePickerState): TimingRepeatValue {
  if (state.endMode === 'onDate' && state.endDate) {
    timing.boundsPeriod = { ...timing.boundsPeriod, end: parseISO(state.endDate) };
  } else if (state.endMode === 'afterCount' && state.scheduleType !== 'daysPerWeek') {
    timing.count = Math.max(1, Math.round(state.endCount));
  }
  return timing;
}

export function buildTimingRepeat(state: SchedulePickerState): TimingRepeatValue | null {
  const { scheduleType, selectedDays, daysPerWeekCount, customInterval, customUnit, specificTime } = state;
  // 'asNeeded' (PRN) and 'once' carry no repeating timing. They're distinguished from
  // each other on the resource (AS_NEEDED extension / due date), not in the timing repeat.
  if (scheduleType === 'once' || scheduleType === 'asNeeded') return null;

  if (scheduleType === 'simple') {
    const dayOfWeek = DAY_KEYS.filter((_, i) => selectedDays[i]) as TimingRepeatValue['dayOfWeek'];
    if (!dayOfWeek || dayOfWeek.length === 0) return null;

    // Canonical weekday shape: a period-1 day cadence filtered to the selected
    // days, with `frequency` counting times per day (strict FHIR — see
    // docs/design-decisions/2026-07-01-canonical-care-plan-timing-shapes.md).
    // Replaces the legacy `periodUnit: 'wk'` boilerplate-trio encoding.
    const timing: TimingRepeatValue = { frequency: 1, period: 1, periodUnit: 'd', dayOfWeek };
    if (state.whenCodes.length > 0) {
      // Chips are the only source of per-day multiplicity: N codes = N×/day.
      timing.when = sortWhenCodes(state.whenCodes);
      timing.frequency = timing.when.length;
    } else if (specificTime) {
      timing.timeOfDay = [specificTime];
    }
    return withScheduleEnd(withStartDate(timing, state.startDate), state);
  }

  if (scheduleType === 'daysPerWeek') {
    // Weekly quota: complete on any N distinct days per week. `frequency`
    // counts days per week on this shape (no dayOfWeek). 7 days per week is
    // daily, so it normalizes to the weekday shape.
    const count = Math.min(MAX_DAYS_PER_WEEK, Math.max(1, Math.round(daysPerWeekCount)));
    if (count >= MAX_DAYS_PER_WEEK) {
      return withScheduleEnd(
        withStartDate(withTimeOfDay(dailyAllDays(), state.whenCodes[0] ?? ''), state.startDate),
        state,
      );
    }
    return withScheduleEnd(
      withStartDate(
        withTimeOfDay({ frequency: count, period: 1, periodUnit: 'wk' }, state.whenCodes[0] ?? ''),
        state.startDate,
      ),
      state,
    );
  }

  // Custom = "every {x} hours|days|weeks|months|years" interval. No
  // day-of-week; one occurrence per period. "Every 1 day" is daily, so it
  // normalizes to the weekday shape.
  const interval = Math.max(1, Math.round(customInterval));
  if (customUnit === 'hours') {
    // Preset-only per the design decision; no time-of-day annotation — the
    // canonical hourly shape carries no modifiers.
    return withScheduleEnd(
      withStartDate({ frequency: 1, period: nearestHourlyPreset(interval), periodUnit: 'h' }, state.startDate),
      state,
    );
  }
  if (customUnit === 'days' && interval === 1) {
    return withScheduleEnd(
      withStartDate(withTimeOfDay(dailyAllDays(), state.whenCodes[0] ?? ''), state.startDate),
      state,
    );
  }
  return withScheduleEnd(
    withStartDate(
      withTimeOfDay(
        { frequency: 1, period: interval, periodUnit: CUSTOM_UNIT_TO_PERIOD_UNIT[customUnit] },
        state.whenCodes[0] ?? '',
      ),
      state.startDate,
    ),
    state,
  );
}

function extractStartDate(timing: InitialTimingValue): string {
  if (!timing.boundsPeriod?.start) return '';
  const d =
    timing.boundsPeriod.start instanceof Date ? timing.boundsPeriod.start : parseISO(String(timing.boundsPeriod.start));
  return format(d, DATE_FORMATS.ISO_DATE);
}

/** How an existing timing ends, for edit-mode state. `count` wins over an end
 *  date when both are present, matching the sentence and engine precedence. */
function deriveEndState(
  timing: InitialTimingValue,
  // The days-per-week quota passes false: the engine ignores `count` for that
  // shape and the writer never emits it, so preferring a stored `count` there
  // would silently discard a real, enforced `boundsPeriod.end`.
  allowCount: boolean,
): Pick<SchedulePickerState, 'endMode' | 'endDate' | 'endCount'> {
  if (allowCount && timing.count && timing.count > 0) {
    return { endMode: 'afterCount', endDate: '', endCount: timing.count };
  }
  const end = timing.boundsPeriod?.end;
  if (end) {
    const endDate = end instanceof Date ? end : parseISO(String(end));
    return { endMode: 'onDate', endDate: format(endDate, DATE_FORMATS.ISO_DATE), endCount: DEFAULT_END_COUNT };
  }
  return { endMode: 'never', endDate: '', endCount: DEFAULT_END_COUNT };
}

/** Retained specific clock time from stored `timeOfDay` data (chips replace it on edit). */
function deriveSpecificTime(timing: InitialTimingValue): string {
  return timing.timeOfDay?.[0] ?? '';
}

/**
 * The chips a weekday timing selects: its stored `when[]` codes (any subset —
 * the chips model is strictly more expressive than the old 2×/3×/4× presets),
 * or the canonical default codes when a bare "N times per day" was stored
 * with no codes. Weekly-unit shapes are excluded from the bare-frequency
 * normalization: legacy data used a bare frequency there to mean "N times per
 * week" (now the days-per-week quota shape).
 */
function deriveWhenCodes(timing: InitialTimingValue, unit: string): TimeOfDayCode[] {
  if (timing.when && timing.when.length > 0) {
    return sortWhenCodes(timing.when.filter((w): w is TimeOfDayCode => FHIR_WHEN_CODES.has(w)));
  }
  if (unit === 'd' && timing.frequency !== undefined) {
    return DEFAULT_WHEN_BY_COUNT[timing.frequency] ?? [];
  }
  return [];
}

/**
 * Derive picker state from an existing TimingRepeat (for edit mode).
 *
 * Custom mode only models `every {x} days|weeks|months|years` and drops
 * `dayOfWeek`/`when`/`timeOfDay`. Timings carrying those fields (e.g.
 * period=2 with dayOfWeek) are surfaced as a best-effort Custom state;
 * re-saving overwrites them. `isLegacyCustomTiming` flags this case so
 * `TaskTimingPicker` can warn the user before that happens.
 *
 * Both weekday encodings surface as Simple: the canonical period-1 `'d'`
 * cadence and the legacy `'wk'` + dayOfWeek boilerplate (re-saving rewrites
 * the latter to `'d'`). The days-per-week quota shape surfaces as its own
 * mode carrying the count.
 */
export function deriveStateFromTiming(timing: InitialTimingValue): SchedulePickerState {
  const unit = timing.periodUnit ?? 'd';
  const period = timing.period ?? 1;

  // Hourly timing → the Every… mode with the hours unit. Non-preset legacy
  // periods snap to the nearest preset (the legacy callout warns the rewrite).
  if (unit === 'h') {
    return {
      scheduleType: 'custom',
      selectedDays: makeDaySelection(),
      daysPerWeekCount: DEFAULT_DAYS_PER_WEEK,
      customInterval: nearestHourlyPreset(period),
      customUnit: 'hours',
      specificTime: '',
      whenCodes: [],
      startDate: extractStartDate(timing),
      ...deriveEndState(timing, true),
    };
  }

  // Days-per-week quota (`{frequency: N, period: 1, periodUnit: 'wk'}`, no
  // modifiers) → its own mode carrying the count. Covers first-class writes
  // and legacy "N times per week" data alike. Reader defaults are applied
  // first (mirroring `isLegacyCustomTiming`) so a sparse legacy encoding that
  // omits `period` isn't misrouted to Custom "every 1 week", silently
  // dropping the count.
  const quotaShape = classifyTimingShape({
    frequency: timing.frequency ?? 1,
    period,
    periodUnit: unit,
    dayOfWeek: timing.dayOfWeek,
    when: timing.when,
    timeOfDay: timing.timeOfDay,
  });
  if (quotaShape === 'daysPerWeek') {
    return {
      scheduleType: 'daysPerWeek',
      selectedDays: makeDaySelection(),
      daysPerWeekCount: timing.frequency ?? DEFAULT_DAYS_PER_WEEK,
      customInterval: 1,
      customUnit: 'weeks',
      specificTime: '',
      whenCodes: timing.when?.[0] ? [timing.when[0] as TimeOfDayCode] : [],
      startDate: extractStartDate(timing),
      ...deriveEndState(timing, false),
    };
  }

  const selectedDays = makeDaySelection(timing.dayOfWeek);
  const hasDayOfWeek = !!timing.dayOfWeek && timing.dayOfWeek.length > 0;

  // Simple = a period-1 day cadence ('d', dayOfWeek optional) or the legacy
  // weekly encoding ('wk' with explicit dayOfWeek). Everything else is Custom
  // (including legacy custom shapes).
  // Note: a daily frequency above the multi-daily presets (e.g. 5×/day) still lands
  // in Simple with frequency 'any' while `isLegacyCustomTiming` flags it — the Simple
  // view and the legacy callout coexist, warning that re-saving drops the count.
  let scheduleType: ScheduleType;
  let customInterval = 1;
  let customUnit: CustomUnit = 'weeks';

  if (period === 1 && (unit === 'd' || (unit === 'wk' && hasDayOfWeek))) {
    scheduleType = 'simple';
  } else {
    scheduleType = 'custom';
    customInterval = Math.max(1, period);
    customUnit = PERIOD_UNIT_TO_CUSTOM_UNIT[unit] ?? 'weeks';
  }

  return {
    scheduleType,
    selectedDays,
    daysPerWeekCount: DEFAULT_DAYS_PER_WEEK,
    customInterval,
    customUnit,
    specificTime: scheduleType === 'custom' ? '' : deriveSpecificTime(timing),
    // Chips: full when[] subsets on the weekday shape; a single annotation on
    // intervals.
    whenCodes:
      scheduleType === 'custom'
        ? timing.when?.[0]
          ? [timing.when[0] as TimeOfDayCode]
          : []
        : deriveWhenCodes(timing, unit),
    startDate: extractStartDate(timing),
    ...deriveEndState(timing, true),
  };
}

/**
 * True when an existing timing cannot be round-tripped by the editor: any
 * non-canonical shape per `classifyTimingShape` (ambiguous legacy data like
 * "N times per month", mismatched when counts, non-preset hourly intervals),
 * plus canonical weekday timings whose bare times-per-day exceeds 4× — the
 * four time-of-day chips top out there, so re-saving would silently drop
 * the count.
 *
 * Used by `TaskTimingPicker` to surface a one-line callout warning the user
 * that re-saving will overwrite those fields. The classifier requires the
 * full cadence trio, so reader defaults (`?? 1`, `?? 'd'`) are applied first —
 * old stored shapes aren't flagged merely for omitting boilerplate.
 */
export function isLegacyCustomTiming(timing: InitialTimingValue): boolean {
  const { frequency, period, periodUnit, dayOfWeek, when, timeOfDay } = timing;
  const shape = classifyTimingShape({
    frequency: frequency ?? 1,
    period: period ?? 1,
    periodUnit: periodUnit ?? 'd',
    dayOfWeek,
    when,
    timeOfDay,
  });
  if (shape === 'nonCanonical') return true;

  const hasWhen = !!when && when.length > 0;
  const hasTimeOfDay = !!timeOfDay && timeOfDay.length > 0;
  return shape === 'daysOfWeek' && !hasWhen && !hasTimeOfDay && (frequency ?? 1) > 4;
}

export function makeDefaultState(defaultScheduleType: ScheduleType): SchedulePickerState {
  return {
    scheduleType: defaultScheduleType,
    selectedDays: makeDaySelection(),
    daysPerWeekCount: DEFAULT_DAYS_PER_WEEK,
    customInterval: 1,
    customUnit: 'weeks',
    specificTime: '',
    whenCodes: [],
    startDate: '',
    endMode: 'never',
    endDate: '',
    endCount: DEFAULT_END_COUNT,
  };
}
